import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import CreditsAPI, { CreditsBalance } from '../api/credits';
import { isRateLimited } from '../api/rateLimitPause';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL_MS = 30_000;

interface CreditsState {
  balance: number;
  reserved: number;
  dailyLimit: number;
  isAdmin: boolean;
  loaded: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const fallback: CreditsState = {
  balance: 0,
  reserved: 0,
  dailyLimit: 0,
  isAdmin: false,
  loaded: false,
  error: null,
  refresh: async () => {},
};

const CreditsContext = createContext<CreditsState>(fallback);

export const CreditsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState<CreditsBalance | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const fresh = await CreditsAPI.getMyBalance();
      if (!mountedRef.current) return;
      setData(fresh);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err instanceof Error ? err.message : 'failed to load credits';
      setError(msg);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      setData(null);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }
    refresh();
    // A tick that lands while the API has asked (429) to be left alone is skipped.
    const timer = window.setInterval(() => {
      if (!isRateLimited()) void refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
    };
  }, [isAuthenticated, refresh]);

  const value: CreditsState = {
    balance: data?.balance ?? 0,
    reserved: data?.reserved ?? 0,
    dailyLimit: data?.daily_limit ?? 0,
    isAdmin: data?.is_admin ?? false,
    loaded: data !== null,
    error,
    refresh,
  };

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
};

export function useCredits(): CreditsState {
  return useContext(CreditsContext);
}
